import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useFocusTrap } from './useFocusTrap.ts';
// The first import here that is not React or a sibling. `shared/src/icons.tsx`
// is the deck-side lucide wrapper, and lucide-react is a ROOT dependency
// precisely so both builds that mount this file — the studio and the workbench —
// resolve it. A relative path, not the `@shared` alias, because the workbench
// and the studio configure that alias separately and this file predates both.
import { X } from '../src/icons.tsx';

type SheetProps = {
  /** Heading, and what the dialog is labelled by. */
  title: string;
  onClose: () => void;
  children?: ReactNode;
  /** Extra class on the panel, for a host that needs its own padding rules. */
  className?: string;
};

/**
 * A bottom-anchored sheet: the narrow-screen answer to "these controls no
 * longer fit". Backdrop click or Escape closes; focus is trapped while open
 * and handed back to the trigger on close.
 *
 * BOTTOM rather than the right edge, which is where `Drawer` comes from, for
 * three reasons: it is the half of a phone a thumb reaches, the collapsed
 * command bar already lives down there so the two read as one family, and the
 * filmstrip's slide menu needs the same shape — one primitive, several
 * consumers.
 *
 * It is NOT a variant of `Drawer`. Drawer carries a resize handle and a
 * per-title persisted width that a menu has no use for, and it lives under
 * studio/src/client/canvas, which the workbench build cannot reach.
 *
 * The host owns the trigger's `aria-expanded` / `aria-controls`: only the host
 * knows which button opened this. Pass that button's id as the sheet's own id
 * via `aria-labelledby` if the title alone is not enough.
 */
export default function Sheet({ title, onClose, children, className }: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const headingId = useId();
  useFocusTrap(panelRef, true);

  // Escape closes. Kept in a ref-free effect with onClose in the deps so a
  // host that re-creates the callback each render still closes exactly once.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock the page behind the sheet. Restored to whatever it was rather than to
  // '': the workbench's own stylesheet sets `overflow: hidden` on body, and
  // clearing it outright would leave that app scrollable after every close.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    <div className="app-sheet-backdrop" onMouseDown={onClose}>
      <div
        ref={panelRef}
        className={'app-sheet' + (className ? ' ' + className : '')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="app-sheet__grab" aria-hidden="true" />
        <header className="app-sheet__head">
          <h2 className="app-sheet__title" id={headingId}>
            {title}
          </h2>
          <button type="button" className="iconbtn" onClick={onClose} aria-label="Close" title="Close">
            <X />
          </button>
        </header>
        <div className="app-sheet__body">{children}</div>
      </div>
    </div>
  );
}
